import { getIndustries, IIndustryWithPolicyTypes } from "@/api/industries";
import { SearchField } from "@/components/admin";
import { Table } from "@/components/shared/table/Table";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { formatDate } from "@/utils";
import { Chip, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-pro";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { DeleteIndustryButton } from "./DeleteIndustryButton";
import { IndustryDetailsButton } from "./IndustryDetailsButton";

const columns: GridColDef<IIndustryWithPolicyTypes>[] = [
    {
        field: "id",
        headerName: "ID",
        display: "flex",
        width: 80,
    },
    {
        field: "name",
        headerName: "Name",
        display: "flex",
        flex: 1,
        minWidth: 250,
        renderCell: ({ row }) => <IndustryDetailsButton industry={row} />,
    },
    {
        field: "naic",
        headerName: "NAIC",
        display: "flex",
        width: 100,
    },
    {
        field: "policy_types",
        headerName: "Policy Types",
        display: "flex",
        flex: 1,
        minWidth: 200,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                {row.policy_types.slice(0, 3).map((pt) => (
                    <Chip key={pt.id} label={pt.short_name} size="small" />
                ))}
                {row.policy_types.length > 3 && (
                    <Chip
                        label={`+${row.policy_types.length - 3}`}
                        size="small"
                        variant="outlined"
                    />
                )}
            </Stack>
        ),
    },
    {
        field: "skill",
        headerName: "Skill",
        display: "flex",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) =>
            row.skill ? (
                <Chip
                    label={row.skill.name.replace("Industry: ", "")}
                    size="small"
                    color="success"
                    variant="outlined"
                    sx={{ maxWidth: 100 }}
                />
            ) : (
                <span style={{ color: "#9e9e9e", fontSize: "0.8rem" }}>—</span>
            ),
    },
    {
        field: "updated_at",
        headerName: "Last Updated",
        type: "date",
        display: "flex",
        width: 150,
        valueFormatter: (value) => formatDate(value),
    },
    {
        field: "actions",
        headerName: "Actions",
        type: "actions",
        getActions: ({ row }) => [<DeleteIndustryButton industryId={row.id} />],
    },
];

export const IndustriesTable = () => {
    const [rows, setRows] = useState<IIndustryWithPolicyTypes[]>([]);

    const {
        search,
        debouncedSearch,
        setSearch,
        clear: handleSearchClear,
    } = useDebouncedSearch();

    const { data, isFetching } = useQuery({
        queryKey: ["admin-industries"],
        queryFn: getIndustries,
    });

    useEffect(() => {
        if (data) {
            setRows(data);
        }
    }, [data]);

    const filteredRows = useMemo(() => {
        if (!debouncedSearch) {
            return rows;
        }
        const q = debouncedSearch.toLowerCase();
        return rows.filter(
            (row) =>
                row.name.toLowerCase().includes(q) ||
                row.naic.toLowerCase().includes(q)
        );
    }, [rows, debouncedSearch]);

    return (
        <>
            <div className="mb-4">
                <SearchField
                    value={search}
                    onChange={setSearch}
                    onClear={handleSearchClear}
                    placeholder="Search industries..."
                />
            </div>
            <Table
                loading={isFetching}
                columns={columns}
                rows={filteredRows}
                disableColumnFilter
                pageSizeOptions={[10, 25, 50]}
            />
        </>
    );
};
